import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { connect } from 'react-redux';
import * as queries from './graphql/query';
import log from '../../utils/ClientLogger';
import LeadActions from '../../actions/lead';
import { setPartnerTuid } from '../../utils/EventProviders/EgClickstreamUtil';

export const UserCtx = createContext();

export function UserContext(props) {
    const {
        children
    } = props;
    const [epcUserId, setEpcUserId] = useState(null);
    const [aapEligibility, setAapEligibility] = useState(null);
    const [paperContractUser, setPaperContractUser] = useState(null);
    const [basicUserInformation, setBasicUserInformation] = useState(null);

    const [loading, setLoading] = useState(false);

    // generic on error function for info retrieval
    const infoRetrievalOnError = (functionName) => (error) => {
        log(['error'], functionName, error);
    };

    // <-- basic user information block -->
    // here the basic information of the user is fetched and stored in the state and redux

    // onComplete function for basic user information call
    const onBasicComplete = (payload) => {
        const {
            errors: resultError,
            userId,
            userInformation
        } = payload.fetchBasicUserInformation;
        if (resultError) {
            log(['info'], 'fetchBasicUserInformation', resultError);
        } else {
            setEpcUserId(userId);
            setPartnerTuid(userId);
            setBasicUserInformation(userInformation);
        }
    };
    // lazy query to fetch basic user information
    const [
        fetchBasicUserInformation,
        fetchBasicUserInformationCallState
    ] = useLazyQuery(queries.FETCH_BASIC_USER_INFORMATION, {
        onCompleted: onBasicComplete,
        onError: infoRetrievalOnError('fetchBasicUserInformation')
    });

    // fetches the basic user information from pups
    const getBasicUserInformation = async () => {
        if (basicUserInformation) {
            return {
                epcUserId,
                ...basicUserInformation
            };
        }
        const response = await fetchBasicUserInformation();
        return {
            epcUserId: response?.data?.fetchBasicUserInformation?.userId,
            ...response?.data?.fetchBasicUserInformation?.userInformation
        };
    };

    // sets the basic user information in redux state when basicUserInformation is fetched
    const {
        setEmail,
        setFirstName,
        setLastName,
        setLatinFirstName,
        setLatinLastName,
        setPhoneCountryCodeDigits,
        setPhoneNumber
    } = props;
    useEffect(() => {
        if (basicUserInformation) {
            if (basicUserInformation.email) setEmail(basicUserInformation.email);
            // eslint-disable-next-line max-len
            if (basicUserInformation.asciiFirstName ?? basicUserInformation.firstName) setFirstName(basicUserInformation.asciiFirstName ?? basicUserInformation.firstName);
            // eslint-disable-next-line max-len
            if (basicUserInformation.asciiLastName ?? basicUserInformation.lastName) setLastName(basicUserInformation.asciiLastName ?? basicUserInformation.lastName);
            if (basicUserInformation.firstName) setLatinFirstName(basicUserInformation.firstName);
            if (basicUserInformation.lastName) setLatinLastName(basicUserInformation.lastName);
            if (basicUserInformation.phoneCountryCode) setPhoneCountryCodeDigits(basicUserInformation.phoneCountryCode);
            if (basicUserInformation.phoneNumber) setPhoneNumber(basicUserInformation.phoneNumber);
        }
    }, [basicUserInformation]);

    // <-- AAP user information block -->
    // here the aap eligibility is fetched and stored in the state

    const aapOnComplete = (payload) => {
        const {
            aapEligibility: aapEligibilityValue
        } = payload.fetchAapUserInformation;

        setAapEligibility(aapEligibilityValue);
    };
    const [
        fetchAapUserInformation,
        fetchAapUserInformationCallState
    ] = useLazyQuery(queries.FETCH_AAP_USER_INFORMATION, {
        onCompleted: aapOnComplete,
        onError: infoRetrievalOnError('FetchAapUserInformation')
    });

    // fetches aap user information
    // returns the basic info + the newly fetched one
    const getAapUserInformation = async () => {
        const {
            epcUserId: currentEpcUserId,
            ...basicInfo
        } = await getBasicUserInformation();
        if (aapEligibility !== null) {
            return {
                epcUserId: currentEpcUserId,
                aapEligibility,
                ...basicInfo
            };
        }

        const response = await fetchAapUserInformation();
        return {
            aapEligibility: response?.data?.fetchAapUserInformation?.aapEligibility,
            epcUserId: currentEpcUserId,
            ...basicInfo
        };
    };

    // <-- Paper contract user check -->
    const { setIsPaperContractUser } = props;
    const [
        checkForPaperContractUser,
        checkForPaperContractUserCallState
    ] = useLazyQuery(queries.IS_PAPER_CONTRACT_USER, {
        onCompleted: (response) => {
            setPaperContractUser(response.isEpcPaperContractUser); // sets it to context state
            setIsPaperContractUser(response.isEpcPaperContractUser); // sets it to redux state
        },
        onError: infoRetrievalOnError('IsPaperContractUser')
    });
    // checks for paper contract user if aapEligibility is true
    const isPaperContractUser = async () => {
        if (paperContractUser) {
            return paperContractUser;
        }
        const result = await checkForPaperContractUser();
        return result?.data?.isEpcPaperContractUser;
    };
    useEffect(() => {
        if (aapEligibility) {
            isPaperContractUser();
        }
    }, [aapEligibility]);

    // <-- Loading state management -->
    // sets loading state when any of the calls is loading
    useEffect(() => {
        setLoading(
            fetchAapUserInformationCallState.loading
            || checkForPaperContractUserCallState.loading
            || fetchBasicUserInformationCallState.loading
        );
    }, [
        fetchAapUserInformationCallState.loading,
        checkForPaperContractUserCallState.loading,
        fetchBasicUserInformationCallState.loading
    ]);

    return (
        <UserCtx.Provider value={{
            isPaperContractUser,
            getAapUserInformation,
            getBasicUserInformation,
            paperContractUser,
            loading,
            epcUserId
        }}
        >
            {children}
        </UserCtx.Provider>
    );
}

UserContext.propTypes = {
    // redux hydration
    children: PropTypes.node.isRequired,
    // user information actions
    setEmail: PropTypes.func.isRequired,
    setFirstName: PropTypes.func.isRequired,
    setLastName: PropTypes.func.isRequired,
    setLatinFirstName: PropTypes.func.isRequired,
    setLatinLastName: PropTypes.func.isRequired,
    setPhoneCountryCodeDigits: PropTypes.func.isRequired,
    setPhoneNumber: PropTypes.func.isRequired,
    setIsPaperContractUser: PropTypes.func.isRequired
};

/* istanbul ignore next */
const mapStateToProps = () => ({});

/* istanbul ignore next */
// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({
    setEmail: (email) => dispatch(LeadActions.setEmail(email)),
    setFirstName: (firstName) => dispatch(LeadActions.setFirstName(firstName)),
    setLastName: (lastName) => dispatch(LeadActions.setLastName(lastName)),
    setLatinFirstName: (latinFirstName) => dispatch(LeadActions.setLatinFirstName(latinFirstName)),
    setLatinLastName: (latinLastName) => dispatch(LeadActions.setLatinLastName(latinLastName)),
    setPhoneCountryCodeDigits: (code) => dispatch(LeadActions.setPhoneCountryCodeDigits(code)),
    setPhoneNumber: (number) => dispatch(LeadActions.setPhoneNumber(number)),
    setIsPaperContractUser: (isPaperContractUser) => dispatch(
        LeadActions.setIsPaperContractUser(isPaperContractUser)
    )
});

UserContext.displayName = 'UserContext';

export default connect(mapStateToProps, mapDispatchToProps)(UserContext);
